import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, nextTick, ref } from '@nuxtjs/composition-api';
import groupBy from 'lodash/groupBy';
import type { BravadoSelectDropdownOption, BravadoSelectDropdownOptionImage } from './types';
import { ImageShape } from './types';
import BravadoSelectDropdownItem from './BravadoSelectDropdownItem.vue';
import BravadoSelectDropdownImage from './BravadoSelectDropdownImage.vue';
import { focusOnNextOption, focusOnPreviousOption, keys } from './accessibility';
import PlusIcon from '~/assets/svg/plus.vue.svg';

type BravadoSelectDropdownProps = {
  showOptions?: boolean;
  shouldFocusOnOpen?: boolean;
  isCreatable?: boolean;
  selectedOptions?: Array<BravadoSelectDropdownOption>;
  options?: Array<BravadoSelectDropdownOption>;
  selectId: string;
  term?: string;
  minLength?: number;
  position?: 'up' | 'down';
  getIsRemovable?: (option: BravadoSelectDropdownOption) => boolean;
};
type BravadoSelectDropdownEmits = {
  (event: 'toggle-option', option: BravadoSelectDropdownOption): void;
  (event: 'close-options', payload?: MouseEvent): void;
  (event: 'create-option'): void;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'BravadoSelectDropdown',
  props: {
    showOptions: { type: Boolean, default: false },
    shouldFocusOnOpen: { type: Boolean, default: false },
    isCreatable: { type: Boolean, default: false },
    selectedOptions: { default: () => [] },
    options: { default: () => [] },
    selectId: null,
    term: { default: '' },
    minLength: { default: 2 },
    position: { default: 'down' },
    getIsRemovable: { type: Function, default: () => false }
  },
  emits: ["toggle-option", "close-options", "create-option"],
  setup(__props: any, { emit }: { emit: ({
  (event: 'toggle-option', option: BravadoSelectDropdownOption): void;
  (event: 'close-options', payload?: MouseEvent): void;
  (event: 'create-option'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { showOptions: boolean, shouldFocusOnOpen: boolean, isCreatable: boolean, selectedOptions: Array<BravadoSelectDropdownOption>, options: Array<BravadoSelectDropdownOption>, selectId: string, term: string, minLength: number, position: 'up' | 'down', getIsRemovable: (option: BravadoSelectDropdownOption) => boolean };




const imageForAddingNew: BravadoSelectDropdownOptionImage = {
  shape: ImageShape.rounded,
  component: PlusIcon,
  props: {
    alt: 'Plus',
    width: '20px',
    height: '20px',
  },
};
const dropdown = ref<HTMLElement | null>(null);

function handleToggleOption(option: BravadoSelectDropdownOption) {
  emit('toggle-option', option);
}

const splittedOptions = computed(() =>
  groupBy(props.options, option => (props.getIsRemovable(option) ? 'removableOptions' : 'nonRemovableOptions')),
);
const optionToFirstFocus = computed(() => {
  const removableSelectedOptions = props.selectedOptions.filter(props.getIsRemovable);
  return [...removableSelectedOptions, ...splittedOptions.value.removableOptions][0];
});

function isElementToFocus(option: BravadoSelectDropdownOption) {
  return optionToFirstFocus.value.selectDropdownValue === option.selectDropdownValue;
}

const rootClass = computed(() => {
  return {
    'bravado-select-dropdown': true,
    'bravado-select-dropdown--position-up': props.position === 'up',
    'bravado-select-dropdown--position-down': props.position === 'down',
  };
});

const isOptionSelected = computed(() =>
  props.selectedOptions.reduce((acc, option) => {
    acc[option.selectDropdownValue] = true;
    return acc;
  }, {} as Record<string, boolean>),
);

const optionsElements = computed(() =>
  Array.from(document.getElementsByClassName('bravado-select-dropdown__item') as HTMLCollectionOf<HTMLElement>).filter(
    element => !element.classList.contains('bravado-select-dropdown-item--not-removable'),
  ),
);

function closeOnClickOutside(event: MouseEvent) {
  emit('close-options', event);
}

function handleKeyDown(event: KeyboardEvent, option: BravadoSelectDropdownOption) {
  const callbacks = {
    [keys.ARROW_UP.keyName]: () => focusOnPreviousOption(optionsElements.value),
    [keys.ARROW_DOWN.keyName]: () => focusOnNextOption(optionsElements.value),
    [keys.ENTER.keyName]: () => handleToggleOption(option),
    [keys.SPACE.keyName]: () => handleToggleOption(option),
    [keys.ESCAPE.keyName]: () => emit('close-options'),
  };

  callbacks[event.key]?.();
}

onMounted(() => {
  if (props.shouldFocusOnOpen) {
    const element = document.querySelector('.bravado-select-dropdown__item[tabindex="0"]') as HTMLElement;
    if (!element) return;
    nextTick(() => {
      element.focus();
    });
  }
});

return { __sfc: true,props, emit, imageForAddingNew, dropdown, handleToggleOption, splittedOptions, optionToFirstFocus, isElementToFocus, rootClass, isOptionSelected, optionsElements, closeOnClickOutside, handleKeyDown, BravadoSelectDropdownItem, BravadoSelectDropdownImage }
}

})